import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Cake as CakeIcon,
  School as SchoolIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { SingleImageDisplay } from '../../components/ImageDisplay';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface Child {
  _id: string;
  fullName: string;
  birthDate: string;
  gender: string;
  photo: string;
  kindergarten: {
    _id: string;
    name: string;
  };
  parents: Array<{
    _id: string;
    fullName: string;
  }>;
  allergies: Array<{
    name: string;
    description: string;
    severity: string;
  }>;
  specialNeeds: Array<{
    type: string;
    description: string;
    recommendations: string;
  }>;
  medicalConditions: Array<{
    name: string;
    description: string;
    medications: Array<{
      name: string;
      dosage: string;
      schedule: string;
    }>;
  }>;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

interface Kindergarten {
  _id: string;
  name: string;
}

interface Parent {
  _id: string;
  fullName: string;
}

interface ChildFormData {
  fullName: string;
  birthDate: string;
  gender: string;
  photo: File | null;
  kindergartenId: string;
  parentIds: string[];
  allergies: Array<{
    name: string;
    description: string;
    severity: string;
  }>;
  specialNeeds: Array<{
    type: string;
    description: string;
    recommendations: string;
  }>;
  medicalConditions: Array<{
    name: string;
    description: string;
    medications: Array<{
      name: string;
      dosage: string;
      schedule: string;
    }>;
  }>;
  notes: string;
}

const Children: React.FC = () => {
  const { hasPermission } = useAdminAuth();
  const [children, setChildren] = useState<Child[]>([]);
  const [kindergartens, setKindergartens] = useState<Kindergarten[]>([]);
  const [parents, setParents] = useState<Parent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingChild, setEditingChild] = useState<Child | null>(null);
  const [viewingChild, setViewingChild] = useState<Child | null>(null);
  const [formData, setFormData] = useState<ChildFormData>({
    fullName: '',
    birthDate: '',
    gender: 'чоловіча',
    photo: null,
    kindergartenId: '',
    parentIds: [],
    allergies: [],
    specialNeeds: [],
    medicalConditions: [],
    notes: '',
  });

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [childrenRes, kindergartensRes, parentsRes] = await Promise.all([
        api.get(`/children?page=${page}&limit=${itemsPerPage}`),
        api.get('/kindergartens?limit=1000'),
        api.get('/parents?limit=1000'),
      ]);
      
      setChildren(childrenRes.data.data || []);
      setKindergartens(kindergartensRes.data.data || []);
      setParents(parentsRes.data.data || []);
      
      // Встановлюємо дані пагінації
      if (childrenRes.data.pagination) {
        setTotalPages(childrenRes.data.pagination.totalPages);
        setTotalItems(childrenRes.data.pagination.totalItems);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (child?: Child) => {
    if (child) {
      setEditingChild(child);
      setFormData({
        fullName: child.fullName,
        birthDate: child.birthDate.split('T')[0], // Конвертуємо в формат YYYY-MM-DD
        gender: child.gender || 'чоловіча',
        photo: null,
        kindergartenId: child.kindergarten._id,
        parentIds: child.parents.map(p => p._id),
        allergies: child.allergies,
        specialNeeds: child.specialNeeds,
        medicalConditions: child.medicalConditions,
        notes: child.notes,
      });
    } else {
      setEditingChild(null);
      setFormData({
        fullName: '',
        birthDate: '',
        gender: 'чоловіча',
        photo: null,
        kindergartenId: '',
        parentIds: [],
        allergies: [],
        specialNeeds: [],
        medicalConditions: [],
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingChild(null);
    setFormData({
      fullName: '',
      birthDate: '',
      gender: 'чоловіча',
      photo: null,
      kindergartenId: '',
      parentIds: [],
      allergies: [],
      specialNeeds: [],
      medicalConditions: [],
      notes: '',
    });
  };

  const handleOpenViewDialog = (child: Child) => {
    setViewingChild(child);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingChild(null);
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('birthDate', formData.birthDate);
      formDataToSend.append('gender', formData.gender);
      formDataToSend.append('kindergarten', formData.kindergartenId);
      formDataToSend.append('parents', JSON.stringify(formData.parentIds));
      formDataToSend.append('allergies', JSON.stringify(formData.allergies));
      formDataToSend.append('specialNeeds', JSON.stringify(formData.specialNeeds));
      formDataToSend.append('medicalConditions', JSON.stringify(formData.medicalConditions));
      formDataToSend.append('notes', formData.notes);

      if (formData.photo) {
        formDataToSend.append('photo', formData.photo);
      }

      if (editingChild) {
        await api.put(`/children/${editingChild._id}`, formDataToSend);
      } else {
        await api.post('/children', formDataToSend);
      }

      handleCloseDialog();
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка збереження');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Ви впевнені, що хочете видалити цю дитину?')) {
      try {
        await api.delete(`/children/${id}`);
        fetchData();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Помилка видалення');
      }
    }
  };

  const addItem = (field: 'allergies' | 'specialNeeds' | 'medicalConditions') => {
    const newItem = prompt(`Введіть новий ${field === 'allergies' ? 'алерген' : field === 'specialNeeds' ? 'особливість' : 'медичну умову'}:`);
    if (newItem && newItem.trim()) {
      if (field === 'allergies') {
        setFormData(prev => ({
          ...prev,
          allergies: [...prev.allergies, {
            name: newItem.trim(),
            description: '',
            severity: 'середня'
          }]
        }));
      } else if (field === 'specialNeeds') {
        setFormData(prev => ({
          ...prev,
          specialNeeds: [...prev.specialNeeds, {
            type: newItem.trim(),
            description: '',
            recommendations: ''
          }]
        }));
      } else if (field === 'medicalConditions') {
        setFormData(prev => ({
          ...prev,
          medicalConditions: [...prev.medicalConditions, {
            name: newItem.trim(),
            description: '',
            medications: []
          }]
        }));
      }
    }
  };

  const removeItem = (field: 'allergies' | 'specialNeeds' | 'medicalConditions', index: number) => {
    if (field === 'allergies') {
      setFormData(prev => ({
        ...prev,
        allergies: prev.allergies.filter((_, i) => i !== index)
      }));
    } else if (field === 'specialNeeds') {
      setFormData(prev => ({
        ...prev,
        specialNeeds: prev.specialNeeds.filter((_, i) => i !== index)
      }));
    } else if (field === 'medicalConditions') {
      setFormData(prev => ({
        ...prev,
        medicalConditions: prev.medicalConditions.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Діти
        </Typography>
        {hasPermission('children', 'create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => handleOpenDialog()}
          >
            Додати дитину
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Фото</TableCell>
                  <TableCell>ПІБ</TableCell>
                  <TableCell>Вік</TableCell>
                  <TableCell>Дитячий садок</TableCell>
                  <TableCell>Батьки</TableCell>
                  <TableCell>Особливості</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {children.map((child) => (
                  <TableRow key={child._id}>
                    <TableCell>
                      <SingleImageDisplay
                        image={child.photo}
                        size={40}
                        title="Фото дитини"
                        placeholderIcon={<PersonIcon />}
                        placeholderText="Немає фото"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {child.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CakeIcon fontSize="small" color="action" />
                        {calculateAge(child.birthDate)} років
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" color="action" />
                        {child.kindergarten.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {child.parents.map((parent, index) => (
                          <Chip
                            key={parent._id}
                            label={parent.fullName}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {child.parents.length === 0 && (
                          <Chip label="Немає батьків" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {child.allergies.length > 0 && (
                          <Chip 
                            label={`Алергії: ${child.allergies.length}`} 
                            size="small" 
                            color="warning" 
                            icon={<WarningIcon />}
                          />
                        )}
                        {child.specialNeeds.length > 0 && (
                          <Chip 
                            label={`Особливості: ${child.specialNeeds.length}`} 
                            size="small" 
                            color="info" 
                          />
                        )}
                        {child.medicalConditions.length > 0 && (
                          <Chip 
                            label={`Медичні: ${child.medicalConditions.length}`} 
                            size="small" 
                            color="error" 
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Переглянути">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenViewDialog(child)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('children', 'update') && (
                          <Tooltip title="Редагувати">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog(child)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('children', 'delete') && (
                          <Tooltip title="Видалити">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(child._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={(e, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
              size="large"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Діалог додавання/редагування */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingChild ? 'Редагувати дитину' : 'Додати дитину'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Повне ім'я"
              value={formData.fullName}
              onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Дата народження"
              type="date"
              value={formData.birthDate}
              onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Дитячий садок</InputLabel>
              <Select
                value={formData.kindergartenId}
                onChange={(e) => setFormData(prev => ({ ...prev, kindergartenId: e.target.value }))}
                label="Дитячий садок"
              >
                {kindergartens.map((kindergarten) => (
                  <MenuItem key={kindergarten._id} value={kindergarten._id}>
                    {kindergarten.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Стать</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                label="Стать"
              >
                <MenuItem value="чоловіча">Чоловіча</MenuItem>
                <MenuItem value="жіноча">Жіноча</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Батьки</InputLabel>
              <Select
                multiple
                value={formData.parentIds}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  parentIds: typeof e.target.value === 'string' ? [] : e.target.value 
                }))}
                label="Батьки"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((parentId) => {
                      const parent = parents.find(p => p._id === parentId);
                      return parent ? (
                        <Chip key={parentId} label={parent.fullName} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {parents.map((parent) => (
                  <MenuItem key={parent._id} value={parent._id}>
                    {parent.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
            
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Фото дитини
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setFormData(prev => ({ ...prev, photo: file }));
              }}
            />
            {formData.photo && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Завантажене фото:
                </Typography>
                <Avatar
                  src={URL.createObjectURL(formData.photo)}
                  sx={{ width: 80, height: 80 }}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Медична інформація
            </Typography>
            
            {/* Алергії */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">Алергії</Typography>
                <Button onClick={() => addItem('allergies')} variant="outlined" size="small">
                  Додати алергію
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {formData.allergies.map((allergy, index) => (
                  <Chip
                    key={index}
                    label={allergy.name}
                    onDelete={() => removeItem('allergies', index)}
                    color="warning"
                  />
                ))}
              </Box>
            </Box>

            {/* Особливі потреби */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">Особливі потреби</Typography>
                <Button onClick={() => addItem('specialNeeds')} variant="outlined" size="small">
                  Додати потребу
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {formData.specialNeeds.map((need, index) => (
                  <Chip
                    key={index}
                    label={need.type}
                    onDelete={() => removeItem('specialNeeds', index)}
                    color="info"
                  />
                ))}
              </Box>
            </Box>

            {/* Медичні умови */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">Медичні умови</Typography>
                <Button onClick={() => addItem('medicalConditions')} variant="outlined" size="small">
                  Додати умову
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {formData.medicalConditions.map((condition, index) => (
                  <Chip
                    key={index}
                    label={condition.name}
                    onDelete={() => removeItem('medicalConditions', index)}
                    color="error"
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Примітки"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingChild ? 'Зберегти' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог перегляду дитини */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Інформація про дитину
        </DialogTitle>
        <DialogContent>
          {viewingChild && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SingleImageDisplay
                  image={viewingChild.photo}
                  size={80}
                  title="Фото дитини"
                  placeholderIcon={<PersonIcon />}
                  placeholderText="Немає фото"
                />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {viewingChild.fullName}
                  </Typography>
                  <Chip 
                    label={viewingChild.gender} 
                    color="primary" 
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Дата народження
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CakeIcon fontSize="small" color="action" />
                    {new Date(viewingChild.birthDate).toLocaleDateString('uk-UA')} 
                    ({calculateAge(viewingChild.birthDate)} років)
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Дитячий садок
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon fontSize="small" color="action" />
                    {viewingChild.kindergarten ? viewingChild.kindergarten.name : 'Не вказано'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Батьки
                </Typography>
                {viewingChild.parents && viewingChild.parents.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {viewingChild.parents.map((parent, index) => (
                      <Chip
                        key={index}
                        label={parent.fullName}
                        size="medium"
                        variant="outlined"
                        icon={<PersonIcon />}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Немає батьків
                  </Typography>
                )}
              </Box>

              {viewingChild.allergies && viewingChild.allergies.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Алергії
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {viewingChild.allergies.map((allergy, index) => (
                        <Chip
                          key={index}
                          label={allergy.name}
                          color="warning"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {viewingChild.specialNeeds && viewingChild.specialNeeds.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Особливі потреби
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {viewingChild.specialNeeds.map((need, index) => (
                        <Chip
                          key={index}
                          label={need.type}
                          color="info"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {viewingChild.medicalConditions && viewingChild.medicalConditions.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Медичні умови
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {viewingChild.medicalConditions.map((condition, index) => (
                        <Chip
                          key={index}
                          label={condition.name}
                          color="error"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {viewingChild.notes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Примітки
                    </Typography>
                    <Typography variant="body2">
                      {viewingChild.notes}
                    </Typography>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Додаткова інформація
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Дата створення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingChild.createdAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Останнє оновлення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingChild.updatedAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Children; 